import { useState, useRef, useEffect, useLayoutEffect, createContext, useContext, cloneElement, isValidElement } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Check } from 'lucide-react';
import type { ReactNode, ReactElement, RefObject } from 'react';

// Context for dropdown state
interface DropdownContextType {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  selectedValue?: string;
  onSelect?: (value: string) => void;
  containerRef: RefObject<HTMLDivElement | null>;
  menuRef: RefObject<HTMLDivElement | null>;
}

const DropdownContext = createContext<DropdownContextType | null>(null);

export function useDropdown() {
  const context = useContext(DropdownContext);
  if (!context) {
    throw new Error('Dropdown components must be used within a Dropdown');
  }
  return context;
}

// Main Dropdown container
interface DropdownProps {
  children: ReactNode;
  value?: string;
  onSelect?: (value: string) => void;
}

export function Dropdown({ children, value, onSelect }: DropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close on click outside (check both trigger and portaled menu)
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node;
      const inContainer = containerRef.current?.contains(target);
      const inMenu = menuRef.current?.contains(target);
      if (!inContainer && !inMenu) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Close on escape
  useEffect(() => {
    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  return (
    <DropdownContext.Provider value={{ isOpen, setIsOpen, selectedValue: value, onSelect, containerRef, menuRef }}>
      <div ref={containerRef} className="relative">
        {children}
      </div>
    </DropdownContext.Provider>
  );
}

// Dropdown Trigger button
interface DropdownTriggerProps {
  children?: ReactNode;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  error?: boolean;
  icon?: ReactNode;
  asChild?: boolean;
}

export function DropdownTrigger({
  children,
  placeholder = 'Select...',
  className = '',
  disabled = false,
  error = false,
  icon,
  asChild = false,
}: DropdownTriggerProps) {
  const { isOpen, setIsOpen } = useDropdown();

  // When asChild is true, clone the child element and attach the click handler directly
  if (asChild && isValidElement(children)) {
    return cloneElement(children as ReactElement<{ onClick?: (e: React.MouseEvent) => void }>, {
      onClick: (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!disabled) setIsOpen(!isOpen);
      },
    });
  }

  return (
    <button
      type="button"
      onClick={() => !disabled && setIsOpen(!isOpen)}
      disabled={disabled}
      className={`
        relative w-full h-12 px-4 ${icon ? 'pl-11' : ''} pr-10
        bg-background-card
        border ${error ? 'border-error' : isOpen ? 'border-primary' : 'border-border'}
        rounded-lg
        text-left
        focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20
        disabled:opacity-50 disabled:cursor-not-allowed
        transition-all duration-200
        ${className}
      `}
    >
      {icon && (
        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-text-secondary">
          {icon}
        </span>
      )}
      <span className={children ? 'text-text-primary' : 'text-text-secondary'}>
        {children || placeholder}
      </span>
      <ChevronDown
        className={`absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-text-secondary transition-transform ${
          isOpen ? 'rotate-180' : ''
        }`}
      />
    </button>
  );
}

// Dropdown Menu container
interface DropdownMenuProps {
  children: ReactNode;
  className?: string;
  align?: 'left' | 'right';
  maxHeight?: string;
}

export function DropdownMenu({
  children,
  className = '',
  align = 'left',
  maxHeight = '240px',
}: DropdownMenuProps) {
  const { isOpen, containerRef, menuRef } = useDropdown();
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0, openUpward: false });

  useLayoutEffect(() => {
    if (isOpen && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const maxMenuH = parseInt(maxHeight) || 240;
      const spaceBelow = window.innerHeight - rect.bottom - 8;
      const spaceAbove = rect.top - 8;
      const openUpward = spaceBelow < maxMenuH && spaceAbove > spaceBelow;
      setPosition({
        top: openUpward ? rect.top - 4 : rect.bottom + 4,
        left: align === 'right' ? rect.right : rect.left,
        width: rect.width,
        openUpward,
      });
    }
  }, [isOpen, align, containerRef, maxHeight]);

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          ref={menuRef}
          initial={{ opacity: 0, y: position.openUpward ? 10 : -10, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: position.openUpward ? 10 : -10, scale: 0.95 }}
          transition={{ duration: 0.15 }}
          className={`
            fixed min-w-[160px]
            bg-background-card border border-border rounded-lg shadow-lg
            overflow-hidden z-[9999]
            ${className}
          `}
          style={{
            top: position.openUpward ? undefined : position.top,
            bottom: position.openUpward ? window.innerHeight - position.top : undefined,
            left: align === 'right' ? undefined : position.left,
            right: align === 'right' ? Math.min(window.innerWidth - position.left, window.innerWidth - 168) : undefined,
            width: Math.max(position.width, 160),
          }}
        >
          <div
            className="py-1 overflow-y-auto"
            style={{ maxHeight }}
          >
            {children}
          </div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}

// Dropdown Item
interface DropdownItemProps {
  children: ReactNode;
  value?: string;
  onClick?: () => void;
  disabled?: boolean;
  icon?: ReactNode;
  variant?: 'default' | 'danger';
  showCheck?: boolean;
  closeOnClick?: boolean;
}

export function DropdownItem({
  children,
  value,
  onClick,
  disabled = false,
  icon,
  variant = 'default',
  showCheck = true,
  closeOnClick = true,
}: DropdownItemProps) {
  const { setIsOpen, selectedValue, onSelect } = useDropdown();
  const isSelected = value !== undefined && value === selectedValue;

  const handleClick = () => {
    if (disabled) return;

    if (value !== undefined && onSelect) {
      onSelect(value);
    }
    onClick?.();
    if (closeOnClick) {
      setIsOpen(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={disabled}
      className={`
        w-full flex items-center gap-3 px-4 py-2.5 text-left text-xs sm:text-sm
        transition-colors
        ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
        ${variant === 'danger'
          ? 'text-error hover:bg-error/10'
          : isSelected
            ? 'bg-primary/10 text-primary'
            : 'text-text-primary hover:bg-background-surface'
        }
      `}
    >
      {icon && <span className="flex-shrink-0">{icon}</span>}
      <span className="flex-1">{children}</span>
      {showCheck && isSelected && (
        <Check className="w-4 h-4 flex-shrink-0" />
      )}
    </button>
  );
}

// Dropdown Checkbox Item (for multi-select filters)
interface DropdownCheckboxItemProps {
  children: ReactNode;
  checked: boolean;
  onChange: () => void;
  disabled?: boolean;
}

export function DropdownCheckboxItem({
  children,
  checked,
  onChange,
  disabled = false,
}: DropdownCheckboxItemProps) {
  return (
    <button
      type="button"
      onClick={() => !disabled && onChange()}
      disabled={disabled}
      className={`
        w-full px-3 py-2 text-left text-xs sm:text-sm flex items-center gap-2
        transition-colors
        ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
        ${checked
          ? 'bg-primary/10 text-primary'
          : 'text-text-primary hover:bg-background-surface'
        }
      `}
    >
      <div
        className={`
          w-4 h-4 rounded border flex items-center justify-center flex-shrink-0
          ${checked ? 'bg-primary border-primary' : 'border-border'}
        `}
      >
        {checked && (
          <Check className="w-3 h-3 text-white" strokeWidth={3} />
        )}
      </div>
      {children}
    </button>
  );
}

// Dropdown Divider
export function DropdownDivider() {
  return <div className="my-1 border-t border-border" />;
}

// Dropdown Label (for grouping)
interface DropdownLabelProps {
  children: ReactNode;
}

export function DropdownLabel({ children }: DropdownLabelProps) {
  return (
    <div className="px-4 py-2 text-xs font-semibold text-text-secondary uppercase tracking-wide">
      {children}
    </div>
  );
}
